import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { fetchGatewaysData, createGatewayBalanceRequest } from '../../services/dataApi';
import PageLoader from '../../components/PageLoader';
import GatewaysTable from '../../components/GatewaysTable';
import AddBalanceRequestModal from '../../components/AddBalanceRequestModal';
import SearchBar from '../../components/SearchBar';
import { searchInData } from '../../utils/searchUtils';
import useUrlPagination from '../../hooks/useUrlPagination';
import useDebouncedValue from '../../hooks/useDebouncedValue';
import '../Page.css';

const AdminGateways = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { currentPage, setCurrentPage, itemsPerPage } = useUrlPagination();
  const [pagination, setPagination] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [balanceGateway, setBalanceGateway] = useState(null);
  const [balanceRequestLoadingRef, setBalanceRequestLoadingRef] = useState('');
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 400);

  const loadData = useCallback(async (page = 1) => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await fetchGatewaysData(page, itemsPerPage, debouncedSearchTerm);
      setData(result.data || result);
      setPagination(result.pagination);
    } catch (err) {
      setError(err.message || 'An error occurred while loading data');
    } finally {
      setLoading(false);
    }
  }, [itemsPerPage, debouncedSearchTerm]);

  useEffect(() => {
    loadData(currentPage);
  }, [currentPage, loadData]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
    setCurrentPage(1);
  };

  // Filter data on client side
  const filteredData = useMemo(() => {
    if (!data) return null;
    
    let gateways = [];
    if (data && data.data && Array.isArray(data.data)) {
      gateways = data.data;
    } else if (Array.isArray(data)) {
      gateways = data;
    } else if (data && data.ops && data.ops.length > 0) {
      const firstOp = data.ops[0];
      if (firstOp.list && Array.isArray(firstOp.list)) {
        gateways = firstOp.list;
      } else if (firstOp.data && firstOp.data.list && Array.isArray(firstOp.data.list)) {
        gateways = firstOp.data.list;
      }
    }

    if (!searchTerm || searchTerm.trim() === '') {
      return data;
    }

    const filtered = searchInData(gateways, searchTerm);
    
    if (data && data.data && Array.isArray(data.data)) {
      return { ...data, data: filtered };
    } else if (Array.isArray(data)) {
      return filtered;
    } else {
      return { ...data, ops: [{ ...data.ops[0], list: filtered }] };
    }
  }, [data, searchTerm]);

  const gatewaysCount = pagination?.total ?? (filteredData?.data?.length || 0);

  const handleBalanceRequest = async ({ amount, gateway }) => {
    const gatewayId = gateway.gatewayId || gateway.GatewayId || gateway.GATEWAY_ID || gateway.id || gateway.Id || gateway.ID || gateway.ref;
    const gatewayRef = gateway.ref || gatewayId;

    if (!gatewayId) {
      return { success: false, error: 'Gateway ID not found' };
    }

    setBalanceRequestLoadingRef(gatewayRef);

    try {
      const result = await createGatewayBalanceRequest({
        gatewayId,
        amount
      });

      if (result.status === true || result.success) {
        await loadData(currentPage);
        return { success: true };
      }

      return {
        success: false,
        error:
          result.error ||
          result.msg ||
          (result && typeof result === 'object' ? JSON.stringify(result) : null) ||
          'Failed to create balance request'
      };
    } catch (err) {
      console.error('Error creating gateway balance request:', err);
      return { success: false, error: err.message || 'An error occurred' };
    } finally {
      setBalanceRequestLoadingRef('');
    }
  };

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-heading">
          <h1>Gateways</h1>
          <p>Review gateway configurations, balances, and trading context in one consistent admin workspace.</p>
        </div>
        <div className="page-overview">
          <div className="overview-card">
            <div className="overview-label">Gateways</div>
            <div className="overview-value">{gatewaysCount}</div>
            <div className="overview-meta">Visible configurations</div>
          </div>
          <div className="overview-card">
            <div className="overview-label">Mode</div>
            <div className="overview-value">Admin</div>
            <div className="overview-meta">Cross-system control</div>
          </div>
        </div>
      </div>
      <div className="page-content">
        <PageLoader loading={loading} error={error}>
          {data ? (
            <div className="page-data">
              <div className="page-section-header">
                <div className="page-section-title">
                  <h2>Gateway Directory</h2>
                  <p>Use search to narrow the list and open any gateway for details.</p>
                </div>
                <div className="page-section-meta">
                  <div className="page-chip">50 gateways per page</div>
                  <div className="page-chip">{searchTerm ? 'Filtered results' : 'Overview mode'}</div>
                </div>
              </div>
              <SearchBar 
                onSearch={handleSearch}
                value={searchTerm}
                resultCount={pagination?.total || 0}
                isUpdating={searchTerm !== debouncedSearchTerm}
                placeholder="Search gateways..."
              />
              <GatewaysTable 
                data={filteredData} 
                showTraderName={false}
                pagination={pagination}
                onPageChange={handlePageChange}
                onAddBalance={(gateway) => setBalanceGateway(gateway)}
                balanceRequestLoadingRef={balanceRequestLoadingRef}
              />
              <AddBalanceRequestModal
                isOpen={!!balanceGateway}
                gateway={balanceGateway}
                onClose={() => setBalanceGateway(null)}
                onSubmit={handleBalanceRequest}
              />
            </div>
          ) : (
            <div className="page-placeholder">
              <p>No data received</p>
            </div>
          )}
        </PageLoader>
      </div>
    </div>
  );
};

export default AdminGateways;
